<template>
	<div class="pik-widget multi-pretest w-full h-full text-sm leading-4">
		<template v-if="isMissingJobnums">
			<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
				<p>Missing Jobnumbers</p>
			</div>
		</template>
		<template v-else-if="isMissingToken">
			<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
				<p>Please <a
					class="underline"
					:href="loginUrl"
					style="text-underline-offset: 4px;"
				>login</a> to complete pretest for credit.</p>
			</div>
		</template>
		<template v-else-if="isError">
			<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
				<p v-html="errorMessage"></p>
			</div>
		</template>
		<template v-else-if="isCheckingStatus">
			<div class="w-full min-height h-full flex flex-row justify-center items-center">
				<LoadingSpinner />
			</div>
		</template>
		<template v-else-if="phase === 'interstitial'">
			<div class="w-full h-full flex flex-col justify-center items-center">
				<div class="w-full max-w-md text-center p-x2 flex flex-col gap-x2">
					<div v-if="interstitialContent" class="flex flex-col gap-x1" v-html="interstitialContent"></div>
					<div class="flex flex-col gap-x1">
						<button
							v-if="hasNextJobnum"
							class="w-full p-x1 laptop:p-x1.5 border-1 border-solid border-black rounded-default bg-black text-white text-sm"
							@click="handleContinueToNext"
							v-html="interstitialContinueButtonLabel"
						></button>
						<button
							@click="handleInterstitialCancel"
							v-html="interstitialCancelButtonLabel"
						></button>
					</div>
				</div>
			</div>
		</template>
		<template v-else-if="phase === 'end'">
			<div class="w-full h-full flex flex-col justify-center items-center">
				<div class="w-full max-w-md text-center p-x2 flex flex-col gap-x2">
					<div v-if="endContent" class="flex flex-col gap-x1" v-html="endContent"></div>
					<div class="flex flex-col gap-x1">
						<button
							v-if="endContinueButtonLabel"
							class="w-full p-x1 laptop:p-x1.5 border-1 border-solid border-black rounded-default bg-black text-white text-sm"
							@click="handleEndContinue"
							v-html="endContinueButtonLabel"
						></button>
						<button
							:class="{ 
								'w-full p-x1 laptop:p-x1.5 border-1 border-solid border-black rounded-default bg-black text-white text-sm': !endContinueButtonLabel 
							}"
							@click="handleEndCancel"
							v-html="endCancelButtonLabel"
						></button>
					</div>
				</div>
			</div>
		</template>
		<template v-else-if="phase === 'success'">
			<div class="w-full h-full flex flex-col justify-center items-center">
				<div class="w-full text-center p-x2 font-medium text-20px bg-black text-white">
					<p>All pretests completed successfully!</p>
				</div>
			</div>
		</template>
		<template v-else-if="currentJobnum">
			<pik-pretest
				:key="currentJobnum"
				:jobnum="currentJobnum"
				:token="token"
				:login-redirect-url="loginRedirectUrl"
				:source="source"
				@is-completed="(event) => handleJobnumCompleted(event)"
				@submit-pretest="(event) => emit('submit-pretest', event)"
				@skip-pretest="(event) => emit('skip-pretest', event)"
				@skipped-all="(event) => emit('skipped-all', event)"
			></pik-pretest>
		</template>
	</div>
</template>

<script setup>
import { defineEmits, defineProps, ref, computed, onMounted, watch } from 'vue'
import { LoadingSpinner } from '@/components/'

const emit = defineEmits([
	'is-completed',
	'submit-pretest',
	'skip-pretest',
	'skipped-all',
	'on-interstitial-cancel',
	'on-end-continue',
	'on-end-cancel',
])

const props = defineProps({
	jobnums: {
		type: [Array, String],
		required: true,
		default: () => [],
	},
	token: {
		type: String,
		default: null,
	},
	loginRedirectUrl: {
		type: String,
		default: 'https://suiteweb.atpointofcare.com/#user/login',
	},
	source: {
		type: String,
		default: 'suite-web',
	},
	interstitialContent: {
		type: String,
		default: 'You have finished this section. Would you like to continue to the next section?',
	},
	interstitialContinueButtonLabel: {
		type: String,
		default: 'Continue',
	},
	interstitialCancelButtonLabel: {
		type: String,
		default: 'Cancel',
	},
	endContent: {
		type: String,
		default: 'You have finished all sections. Would you like to continue?',
	},
	endContinueButtonLabel: {
		type: String,
		default: null,
	},
	endCancelButtonLabel: {
		type: String,
		default: 'Cancel',
	},
})

const pretestStatusEndpoint = computed(() => process.env.VUE_APP_PRETEST_API_URL)

const currentJobnumIndex = ref(0)
const phase = ref(null)
const isCheckingStatus = ref(true)
const isError = ref(false)
const errorMessage = ref(null)
const initialCheckDone = ref(false)

const loginUrl = computed(() => {
	const base = props.loginRedirectUrl
	const params = `redirect=true&redirect_url=${ encodeURIComponent(window.location) }`
	if (base.includes('#')) {
		const split = base.split('#')
		return `${ split[0] }?${ params }#${ split[1] }`
	}
	return `${ base }?${ params }`
})

const isMissingToken = computed(() => {
	return !props.token || (props.token && (props.token === 'undefined' || props.token === 'null'))
})

const jobnumsList = computed(() => {
	const raw = props.jobnums
	if (!raw) return []
	if (Array.isArray(raw)) return raw
	const str = String(raw).trim()
	if (!str) return []
	return str.split(',').map(j => j.trim()).filter(j => j.length > 0)
})

const isMissingJobnums = computed(() => {
	return jobnumsList.value.length === 0
})

const currentJobnum = computed(() => {
	if (currentJobnumIndex.value >= 0 && currentJobnumIndex.value < jobnumsList.value.length) {
		return jobnumsList.value[currentJobnumIndex.value]
	}
	return null
})

const hasNextJobnum = computed(() => {
	return currentJobnumIndex.value < jobnumsList.value.length - 1
})

const isLastJobnum = (jobnum) => {
	const list = jobnumsList.value
	if (!list.length) return false
	const last = list[list.length - 1]
	return String(jobnum) === String(last)
}

const checkJobnumComplete = async (jobnum) => {
	const url = `${ pretestStatusEndpoint.value }/${ jobnum }?token=${ props.token }`
	const req = await fetch(url)
	const res = await req.json()
	if (res.error) throw new Error(res.error)
	if (res?.status?.includes?.('Error')) throw new Error(res.status)
	return res?.status === 'complete'
}

const findFirstIncompleteIndex = async (startFrom = 0) => {
	const list = jobnumsList.value
	for (let i = startFrom; i < list.length; i++) {
		const complete = await checkJobnumComplete(list[i])
		if (!complete) return i
	}
	return list.length
}

const goToNextIncomplete = async (jobnum) => {
	isCheckingStatus.value = true
	isError.value = false
	try {
		const list = jobnumsList.value
		const fromIndex = list.indexOf(jobnum)
		const nextIndex = await findFirstIncompleteIndex(fromIndex + 1)
		currentJobnumIndex.value = nextIndex
		const wasOnLastIndex = fromIndex === list.length - 1
		if (nextIndex >= list.length && wasOnLastIndex) {
			phase.value = 'end'
		}
	} catch (err) {
		isError.value = true
		errorMessage.value = err?.message || err
	} finally {
		isCheckingStatus.value = false
	}
}

const runInitialStatusCheck = async () => {
	const list = jobnumsList.value
	if (list.length === 0) return
	isCheckingStatus.value = true
	isError.value = false
	try {
		const firstIncomplete = await findFirstIncompleteIndex(0)
		currentJobnumIndex.value = firstIncomplete
		if (firstIncomplete >= list.length) {
			phase.value = 'end'
			emit('is-completed', {
				status: 'complete',
				jobnums: list,
				token: props.token,
			})
		} else {
			phase.value = null
		}
	} catch (err) {
		isError.value = true
		errorMessage.value = err?.message || err
	} finally {
		isCheckingStatus.value = false
	}
}

const handleJobnumCompleted = async (event) => {
	if (isCheckingStatus.value) return

	const { jobnum } = event?.detail?.[0]
	const fromIndex = jobnumsList.value.findIndex((j) => String(j) === String(jobnum))
	const nextIndex = await findFirstIncompleteIndex(fromIndex + 1)

	if (nextIndex >= jobnumsList.value.length && isLastJobnum(jobnum)) {
		phase.value = 'end'
	} else {
		phase.value = 'interstitial'
	}
}

const handleContinueToNext = async () => {
	phase.value = null
	await goToNextIncomplete(currentJobnum.value)
}

const handleInterstitialCancel = () => {
	emit('on-interstitial-cancel')
}

const handleEndContinue = () => {
	phase.value = 'success'
	emit('on-end-continue', { status: 'complete', jobnums: jobnumsList.value, token: props.token })
}

const handleEndCancel = () => {
	phase.value = 'success'
	emit('on-end-cancel')
}

onMounted(async () => {
	if (isMissingToken.value) return
	if (jobnumsList.value.length === 0) return
	initialCheckDone.value = true
	await runInitialStatusCheck()
})

watch(
	() => jobnumsList.value.length,
	(len) => {
		if (len > 0 && !initialCheckDone.value && !isMissingToken.value) {
			initialCheckDone.value = true
			runInitialStatusCheck()
		}
	}
)
</script>

<style src="@/assets/css/tailwind.css"></style>
<style src="@/assets/css/_fontface.css"></style>
<style src="@/assets/css/_variables.css"></style>
<style src="@/assets/css/_global.css"></style>
<style src="@/assets/css/_animations.css"></style>
<style src="@/assets/css/main.css"></style>
